import React, { useState, useRef, useEffect } from "react";
import { useInView } from "react-intersection-observer";
import { ReactComponent as ImageIcon } from "../../../assets/icons/image.svg";
import { ReactComponent as VideoRecorderIcon } from "../../../assets/icons/video-recorder.svg";
import {
  Date,
  ModelName,
  VideoElement,
  VideoFooter,
  RunningText,
  LoadingSpinnerBlur,
  CollectionContainer,
  CollectionThumbnail,
  ImageElement,
  IconWrapper,
} from "./CollectionItem.styles";
import { formatModelName } from "../../../utils/utils";
import { format } from "date-fns";
import { Spiral } from "ldrs/react";
import { COLLECTION_STATUS, MEDIA_TYPE } from "../../../utils/constants";

const CollectionItem = ({ video, onCollectionItemClick, priority = false }) => {
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(priority);
  // const autoplayEnabled = useSelector(selectAutoPlay) ?? true;

  const videoRef = useRef(null);

  const { ref: containerRef, inView } = useInView({
    threshold: 0.01,
    rootMargin: "400px",
    triggerOnce: true,
  });

  const inProgress = video.status !== COLLECTION_STATUS.COMPLETED;

  // Delayed video loading za bolje performanse
  useEffect(() => {
    if (inView && !shouldLoadVideo && !inProgress) {
      const timer = setTimeout(
        () => {
          setShouldLoadVideo(true);
        },
        priority ? 0 : 100
      );
      return () => clearTimeout(timer);
    }
  }, [inView, shouldLoadVideo, priority, inProgress]);

  const handleVideoLoad = () => {
    setIsVideoLoaded(true);
  };

  const isImage = video?.output?.type === MEDIA_TYPE.IMAGE;

  return (
    <CollectionContainer
      ref={(el) => {
        containerRef(el);
        // autoplayRef(el);
      }}
      onClick={() => {
        if (onCollectionItemClick && !inProgress) {
          onCollectionItemClick(video);
        }
      }}
      $hasVideoUrl={
        isVideoLoaded && video.status === COLLECTION_STATUS.COMPLETED
      }
      $inProgress={inProgress}
      // onMouseEnter={() => {
      //   if (!autoplayEnabled) {
      //     handleVideoHover();
      //   }
      // }}
      // onMouseLeave={() => {
      //   if (!autoplayEnabled) {
      //     handleVideoLeave();
      //   }
      // }}
    >
      <IconWrapper>
        {isImage ? (
          <ImageIcon color="#ffffff60" />
        ) : (
          <VideoRecorderIcon color="#ffffff60" />
        )}
      </IconWrapper>

      {isImage ? (
        <ImageElement src={video.output.image.url} />
      ) : (
        <>
          <CollectionThumbnail
            src={
              video?.input?.image?.resizedImage?.url || video?.input?.image?.url
            }
            alt={video.title}
            $loaded={isVideoLoaded}
            $blur={!isVideoLoaded}
            loading={priority ? "eager" : "lazy"}
          />
          {shouldLoadVideo && !inProgress && (
            <VideoElement
              ref={videoRef}
              controls={false}
              onLoadedData={handleVideoLoad}
              muted
              loop
              playsInline
              poster={
                video?.input?.image?.resizedImage?.url ||
                video?.input?.image?.url
              }
              autoPlay
              preload="auto"
              $loaded={isVideoLoaded}
            >
              <source
                src={
                  video?.output?.video?.compressedUrl ||
                  video?.output?.video?.url
                }
                type="video/mp4"
              />
            </VideoElement>
          )}
        </>
      )}

      {inProgress && (
        <LoadingSpinnerBlur $show>
          <Spiral size="20" speed="0.9" color="#fff" />
          <RunningText>
            <div className="running-title">Generating...</div>
            {!video.isFree ? (
              <div className="running-message">
                It may take 3-5 minutes <br />
                to generate the video.
              </div>
            ) : (
              <div className="running-message">
                It may take 4 or more minutes to generate the video due to the
                computational costs and the free user queue. <br />
                <br />
                Upgrade your subscription for higher generation priority and
                speed.
              </div>
            )}
          </RunningText>
        </LoadingSpinnerBlur>
      )}

      {!inProgress && (
        <VideoFooter className="video-footer">
          <Date>{format(video.createdAt, "MM/dd/yyyy, h:mm a")}</Date>
          <ModelName>{formatModelName(video.name)}</ModelName>
        </VideoFooter>
      )}
    </CollectionContainer>
  );
};

export default React.memo(CollectionItem);
